import React from 'react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import VibeSelector from '../party/vibe/VibeSelector';
import GoogleAuthButton from '../auth/GoogleAuthButton';
import { createParty } from '../../services/eventLifecycle';
import { JOIN_MODE } from '../../utils/eventStatus';
import { emptyVibeDraft } from '../../vibe/selection';
import useUserStore from '../../stores/userStore';
import { describeError } from '../../utils/errorLog';

const STEP = {
  details: 'details',
  vibe: 'vibe',
};

/**
 * Party creation.
 *
 * Guest access stays here. New parties choose Feel / Style / Flavor before
 * the event exists, and that choice is the host's first saved vibe.
 * Legacy Party Mood is not written.
 *
 * A successful create enters the party immediately. The invite overlay lives
 * on that party route, so closing it does not leave the host on Home.
 */
export default function StartPartyDialog({ open, onClose, onEnterParty }) {
  const isGoogleAccount = useUserStore((state) => state.user?.isGoogleAccount === true);

  const [step, setStep] = React.useState(STEP.details);
  const [title, setTitle] = React.useState('');
  const [joinMode, setJoinMode] = React.useState(JOIN_MODE.open);
  const [creating, setCreating] = React.useState(false);
  const [event, setEvent] = React.useState(null);
  const [draft, setDraft] = React.useState(emptyVibeDraft);

  React.useEffect(() => {
    if (open) return;
    setStep(STEP.details);
    setTitle('');
    setJoinMode(JOIN_MODE.open);
    setCreating(false);
    setEvent(null);
    setDraft(emptyVibeDraft());
  }, [open]);

  const handleContinue = (submitEvent) => {
    submitEvent.preventDefault();
    if (!title.trim()) {
      toast.error('Please name your party');
      return;
    }
    setStep(STEP.vibe);
  };

  const handleCreate = async () => {
    if (creating) return;

    setCreating(true);
    let created = event;
    try {
      if (!created?.id) {
        created = await createParty({
          title,
          guestRequestsEnabled: true,
          joinMode,
          vibe: draft,
        });
        setEvent(created);
        toast.success('Party created');
      }
      onEnterParty?.(created);
    } catch (error) {
      if (created?.id) {
        toast.error('Could not open the party');
        return;
      }
      if (error?.code === 'google-required') {
        toast.error('Sign in with Google to host a party');
      } else if (error?.code === 'missing-title') {
        toast.error('Please name your party');
      } else if (error?.code === 'missing-feel') {
        toast.error('Choose how the party should feel');
      } else {
        console.error('Failed to create party:', describeError(error));
        toast.error('Could not create the party');
      }
    } finally {
      setCreating(false);
    }
  };

  const titleForStep = {
    [STEP.details]: 'Start a Party',
    [STEP.vibe]: 'Choose the vibe',
  }[step];

  return (
    <Modal
      open={open}
      title={titleForStep}
      onClose={onClose}
      layout="stack"
    >
      {step === STEP.details && !isGoogleAccount && (
        <div className="space-y-4">
          <p className="text-sm text-gray-300">
            Hosting needs a Google account so you can reopen this party later and find it in your
            session history. Joining a party never requires signing in.
          </p>
          <GoogleAuthButton label="Continue with Google to host" />
        </div>
      )}

      {step === STEP.details && isGoogleAccount && (
        <form onSubmit={handleContinue} className="space-y-4">
          <div>
            <label htmlFor="partyName" className="block text-sm font-medium text-gray-300 mb-1">
              Party name
            </label>
            <input
              id="partyName"
              type="text"
              value={title}
              onChange={(changeEvent) => setTitle(changeEvent.target.value)}
              placeholder="Friday night"
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
          </div>

          <fieldset className="space-y-2 rounded-lg bg-white/5 p-3">
            <legend className="text-sm text-gray-200">Guest access</legend>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="joinMode"
                value={JOIN_MODE.open}
                checked={joinMode === JOIN_MODE.open}
                onChange={() => setJoinMode(JOIN_MODE.open)}
                className="mt-1 h-4 w-4 accent-blue-500"
              />
              <span className="text-sm text-gray-300">Anyone with the link/code can join</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="joinMode"
                value={JOIN_MODE.approval}
                checked={joinMode === JOIN_MODE.approval}
                onChange={() => setJoinMode(JOIN_MODE.approval)}
                className="mt-1 h-4 w-4 accent-blue-500"
              />
              <span className="text-sm text-gray-300">Require host approval to join</span>
            </label>
          </fieldset>

          <button
            type="submit"
            className="w-full py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            Continue
          </button>
        </form>
      )}

      {step === STEP.vibe && (
        <VibeSelector
          value={draft}
          onChange={setDraft}
          onSave={handleCreate}
          saveLabel={event?.id ? 'Open party' : 'Create Party'}
          saving={creating}
          savingLabel="Starting…"
          compactOptionalPreferences
        />
      )}
    </Modal>
  );
}
